import React from "react";
import translate from "../../i18n/translate";
import { closeFlyout } from "../../static/js/commons";
import { formatDateFullYear } from "../common/helper";
import { WmButton, WmFlyout } from "@watermarkinsights/ripple-react";
import { useIntl } from "react-intl";
import { DrilldownHierarchy } from "./ResponseRate";

export interface DrilldownViewProps {
  setIsDrilldownOpen: (arg0: boolean) => void;
  title: string;
  remainingDays: number;
  startDate: string;
  endDate: string;
  status: number;
  showTable: boolean;
  completeHierarchy: DrilldownHierarchy;
  isVisible: boolean;
  open?: boolean;
  id?: string;
}

export default function OpenDrillDownViewResponseRate({
  setIsDrilldownOpen,
  title,
  remainingDays,
  startDate,
  endDate,
  status,
  showTable,
  completeHierarchy,
  isVisible = false,
  open = false,
  id = "drill-down-view"
}: DrilldownViewProps) {
  const CloseDrillDownView = () => {
    setIsDrilldownOpen(false);
    closeFlyout();
  };

  const closeTooltip = useIntl().formatMessage({
    id: "app.ces.responseRate.drilldownTooltipClose"
  });

  return (
    <WmFlyout
      id={id}
      eyebrow={useIntl().formatMessage({ id: "app.ces.responseRate.drilldownHeading"})}
      heading={useIntl().formatMessage({ id: "app.ces.responseRate.drilldownSubHeading"}) || "No Heading"}
      open={open}
      flyoutWidth="1224px"
      onWmFlyoutCloseTriggered={() => setIsDrilldownOpen(false)}
    >
        <section className="body">
          <div className="flex-row -justified plan-sub-header-container">
            <div className="plan-title-drilldown">{title}</div>
            <CourseEvaluationStatus status={status} />
          </div>
          <div className="ces-duration ces-duration-drilldown">
            <div className="ces-dates">
              <span className="mdi mdi-calendar-clock"></span>
              {`${formatDateFullYear(startDate)} -
                    ${formatDateFullYear(endDate)}`}
            </div>
            {remainingDays > 0 && (
              <div className="ces-days ces-days-drildown">
                <div className="mdi mdi-timer-sand"></div>

                {translate("app.ces.responseRate.daysRemaining", {
                  daysRemaining: remainingDays
                })}
              </div>
            )}
          </div>
          {showTable && completeHierarchy ? (
            <div className="response-rate-table-border">
            <RenderHierarchyTable completeHierarchy={completeHierarchy} />
            </div>
          ) : (
            <div className="loader -halfscreen">
              <div className="content content-loader">
                {translate("app.loading")}
              </div>
            </div>
          )}
        </section>
    </WmFlyout>
  );
}
const CourseEvaluationStatus = ({ status }: { status: number }) => {
  switch (status) {
    case 3:
      return (
        <div className="in-progress-status drilldown-status">
          <div className="title-text">
            {translate("app.drilldown.inProgress")}
          </div>
        </div>
      );
    case 4:
      return (
        <div className="ended-status -completed drilldown-status">
          <div className="title-text">{translate("app.drilldown.ended")}</div>
        </div>
      );
    default:
      return <></>;
  }
};

const getDrilldownColumns = () => {
  return [
    translate("app.drilldown.organizationAndPrograms"),
    translate("app.drilldown.enrollments"),
    translate("app.drilldown.responseRate")
  ];
};
const Table = (props: any) => {
  return <table className="data-table -shadow">{props.children}</table>;
};

const TableHeader = (props: any) => {
  return (
    <thead className="dataheader">
      <tr className="dataheaderrow">
        {props.columns.map((column: string, index: number) => {
          return (
            <th
              className="dataheadercell"
              data-th={column}
              key={`column-${index}`}
            >
              {column}
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

interface HierarchyTableProps {
  completeHierarchy: DrilldownHierarchy;
}

export const RenderHierarchyTable = ({
  completeHierarchy
}: HierarchyTableProps) => {
  return (
    <div className="org-table response-rate-table">
      <Table>
        <TableHeader columns={getDrilldownColumns()} />
        <TableBody>
          {Array.isArray(completeHierarchy) ? (
            completeHierarchy.map((node: DrilldownHierarchy) => {
              return <RenderHierarchy node={node} />;
            })
          ) : (
            <RenderHierarchy node={completeHierarchy} />
          )}
        </TableBody>
      </Table>
    </div>
  );
};
interface HierarchyProps {
  node: DrilldownHierarchy;
}

const RenderHierarchy = ({ node }: HierarchyProps) => {
  return (
    <React.Fragment key={`${node.uuid}-hierarchy`}>
      <Node node={node} />
      {node.children.map((child: DrilldownHierarchy) => {
        return (
          <RenderHierarchy key={`${child.uuid}-hierarchy-child`} node={child} />
        );
      })}
    </React.Fragment>
  );
};

const TableBody = (props: any) => {
  return <tbody className="databody">{props.children}</tbody>;
};

const Node = (props: { node: DrilldownHierarchy }) => {
  const { node } = props;
  return <CompleteRow node={node} />;
};

const CompleteRow = (props: { node: DrilldownHierarchy }) => {
  const { node } = props;

  return (
    <tr className="datarow" key={`${node.uuid}-datarow`}>
      <td className={`datacell level-${node.level}`}>{node.name}</td>
      <td className="datacell">{node.enrollments}</td>
      <td className="datacell">
        {translate("app.drilldown.responseRatePercentage", {
          responseRatePercentage: node.rate
        })}
      </td>
    </tr>
  );
};
